// Chemin: C:\gestion-copro\src\pages\vigie\DashboardVigiePage.tsx
// Dashboard VIGIE — "Charge mentale zéro"
// Session 284 v4 : Refonte esthétique fidèle à la maquette + navigation ciblée sur les signaux

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  collection, query, where, orderBy, limit,
  getDocs, doc, getDoc, updateDoc,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import { isOnboardingComplete } from '../../services/onboarding.service';
import { chargerChecklistCockpit, type ChecklistCockpitData } from '../../services/checklist.service';

import {
  Eye, FileText, FolderOpen, Sparkles, Loader2,
  ArrowRight, ChevronRight, Shield, CheckCircle,
  Home, Building2, Car, Warehouse, BookOpen, Calculator,
  CalendarClock, MessageSquare, Bell, Users,
  Mail, Clock, AlertTriangle, FileQuestion,
  ChevronDown, RotateCcw,
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface QuotaVigie {
  analysesUtilisees: number;
  analysesMax: number;
  stockageUtiliseMo: number;
  stockageMaxMo: number;
  periodeDebut: any;
  periodeFin: any;
  formule: string;
}

interface RecentDocument {
  id: string;
  titre: string;
  type: string;
  createdAt: any;
}

interface RecentCourrier {
  id: string;
  objet: string;
  statut: string;
  destinataire?: string;
  createdAt: any;
  updatedAt?: any;
  envoyeAt?: any;
  dateEnvoi?: any;
  resolu?: boolean;
}

interface LotInfo {
  nature: string;
  tantiemes: number;
}

interface GarantieInfo {
  label: string;
  dateExpiration: Date;
  joursRestants: number;
}

interface ProfilBien {
  lots: LotInfo[];
  totalTantiemes: number;
  totalTantiemesCopro: number | null;
  quotePart: number | null;
  nomSyndic?: string;
  coproprieteNeuve?: boolean;
  garanties: GarantieInfo[];
}

interface EcheanceInfo {
  id: string;
  label: string;
  date: Date;
  joursRestants: number;
}

interface SignalAlerte {
  id: string;
  niveau: 'rouge' | 'orange' | 'gris' | 'bleu';
  icone: React.ReactNode;
  iconeBg: string;
  titre: string;
  description: string;
  badge?: string;
  badgeBg?: string;
  badgeText?: string;
  boutons: { label: string; lien: string; messagePreRempli?: string; style: 'primaire' | 'secondaire' }[];
}

// ============================================
// UTILITAIRES
// ============================================

const formatRelativeTime = (timestamp: any): string => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMs / 3600000);
  const diffJ = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return "A l'instant";
  if (diffMin < 60) return `Il y a ${diffMin} min`;
  if (diffH < 24) return `Il y a ${diffH}h`;
  if (diffJ < 7) return `Il y a ${diffJ}j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const formatDateCourt = (d: Date): string =>
  d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

const formatStockage = (mo: number): string => {
  if (mo < 1024) return `${mo < 10 ? mo.toFixed(1) : Math.round(mo)} Mo`;
  return `${(mo / 1024).toFixed(1)} Go`;
};

const getStatutCourrierConfig = (statut: string): { label: string; bg: string; text: string } => {
  const config: Record<string, { label: string; bg: string; text: string }> = {
    brouillon: { label: 'Brouillon', bg: 'bg-gray-100', text: 'text-gray-600' },
    finalise:  { label: 'Finalise',  bg: 'bg-blue-100',  text: 'text-blue-700' },
    envoye:    { label: 'Envoye',    bg: 'bg-green-100', text: 'text-green-700' },
  };
  return config[statut] || config.brouillon;
};

// ============================================
// LOGIQUE DETECTION ZONE 1
// ============================================

interface DonneesBrutes {
  courriers: RecentCourrier[];
  echeances: EcheanceInfo[];
  profilBien: ProfilBien | null;
  quota: QuotaVigie | null;
  docsManquants: number;
  labelsManquants: string[];
}

function detecterSignaux(data: DonneesBrutes): SignalAlerte[] {
  const signaux: SignalAlerte[] = [];
  const now = new Date();
  const dans10J = new Date(now.getTime() + 10 * 24 * 60 * 60 * 1000);
  const dans30J = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  const dans6M  = new Date(now.getTime() + 6 * 30 * 24 * 60 * 60 * 1000);
  const il_y_a_7J = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  // Rouge : courriers envoyes sans reponse (statut 'envoye' ou 'finalise' > 7j sans reponse)
  const courriersEnvoyesSansReponse = data.courriers.filter(c => {
    if (c.resolu) return false;
    if (c.statut === 'envoye') {
      // Priorité : dateEnvoi > envoyeAt > updatedAt > createdAt
      const dateRef = c.dateEnvoi ?? c.envoyeAt ?? c.updatedAt ?? c.createdAt;
      const d = dateRef?.toDate ? dateRef.toDate() : new Date(dateRef);
      return d < il_y_a_7J;
    }
    if (c.statut === 'finalise') {
      const d = c.createdAt?.toDate ? c.createdAt.toDate() : new Date(c.createdAt);
      return d < il_y_a_7J;
    }
    return false;
  });
  // Un signal rouge par courrier sans reponse — rien ne doit etre cache
  for (const c of courriersEnvoyesSansReponse) {
    const dateRef = c.statut === 'envoye' ? (c.dateEnvoi ?? c.envoyeAt ?? c.updatedAt ?? c.createdAt) : c.createdAt;
    const d = dateRef?.toDate ? dateRef.toDate() : new Date(dateRef);
    const joursDepuis = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    const destinataireLabel = typeof c.destinataire === 'string' ? c.destinataire : (c.destinataire as any)?.nom || 'Interlocuteur';
    signaux.push({
      id: `courrier-sans-reponse-${c.id}`,
      niveau: 'rouge',
      icone: <Mail className="w-4 h-4" style={{ color: '#A32D2D' }} />,
      iconeBg: '#FCEBEB',
      titre: `Courrier sans reponse — ${destinataireLabel}`,
      description: `"${c.objet}" — envoye il y a ${joursDepuis} jour${joursDepuis > 1 ? 's' : ''}, aucune reponse recue.`,
      badge: `${joursDepuis} jour${joursDepuis > 1 ? 's' : ''}`,
      badgeBg: '#FCEBEB',
      badgeText: '#A32D2D',
      boutons: [
        { label: 'Voir le courrier', lien: `/vigie/courriers/${c.id}`, style: 'secondaire' as const },
        { label: 'SENTINELLE — rediger une relance', lien: '/vigie/sentinelle',
          messagePreRempli: `Mon courrier "${c.objet}" envoye il y a ${joursDepuis} jours n'a pas eu de reponse. Aide-moi a rediger une relance SCUD graduee.`,
          style: 'primaire' as const },
      ],
    });
  }

  // Orange : brouillons non envoyes (tous, pas seulement > 7j)
  const tousLesBrouillons = data.courriers.filter(c => c.statut === 'brouillon');
  if (tousLesBrouillons.length > 0) {
    signaux.push({
      id: 'brouillon-en-attente',
      niveau: 'orange',
      icone: <Clock className="w-4 h-4" style={{ color: '#854F0B' }} />,
      iconeBg: '#FAEEDA',
      titre: `${tousLesBrouillons.length} brouillon${tousLesBrouillons.length > 1 ? 's' : ''} en attente d'envoi`,
      description: tousLesBrouillons.map(c => c.objet).join(' · '),
      badge: `${tousLesBrouillons.length} brouillon${tousLesBrouillons.length > 1 ? 's' : ''}`,
      badgeBg: '#FAEEDA',
      badgeText: '#854F0B',
      boutons: [
        ...tousLesBrouillons.map(c => ({
          label: c.objet.length > 40 ? c.objet.slice(0, 40) + '...' : c.objet,
          lien: `/vigie/courriers/${c.id}`,
          style: 'secondaire' as const,
        })),
        { label: 'SENTINELLE — aide-moi a finaliser', lien: '/vigie/sentinelle',
          messagePreRempli: `J'ai ${tousLesBrouillons.length} brouillon${tousLesBrouillons.length > 1 ? 's' : ''} non envoye${tousLesBrouillons.length > 1 ? 's' : ''} : ${tousLesBrouillons.map(c => c.objet).join(', ')}. Aide-moi a les finaliser.`,
          style: 'primaire' as const },
      ],
    });
  }

  // Rouge : echeance < 10j
  const echUrgentes = data.echeances.filter(e => e.date <= dans10J && e.date >= now);
  if (echUrgentes.length > 0) {
    const e = echUrgentes[0];
    signaux.push({
      id: 'echeance-urgente',
      niveau: 'rouge',
      icone: <CalendarClock className="w-4 h-4" style={{ color: '#A32D2D' }} />,
      iconeBg: '#FCEBEB',
      titre: `Echeance dans ${e.joursRestants} jour${e.joursRestants > 1 ? 's' : ''}`,
      description: e.label,
      badge: formatDateCourt(e.date),
      badgeBg: '#FCEBEB',
      badgeText: '#A32D2D',
      boutons: [
        { label: 'Voir le calendrier', lien: '/vigie/calendrier', style: 'secondaire' },
      ],
    });
  }

  // Orange : garantie < 6 mois
  if (data.profilBien) {
    const gProches = data.profilBien.garanties.filter(g => g.joursRestants > 0 && g.dateExpiration <= dans6M);
    if (gProches.length > 0) {
      const g = gProches[0];
      const moisRestants = Math.ceil(g.joursRestants / 30);
      signaux.push({
        id: 'garantie-proche',
        niveau: 'orange',
        icone: <AlertTriangle className="w-4 h-4" style={{ color: '#854F0B' }} />,
        iconeBg: '#FAEEDA',
        titre: `Garantie ${g.label} — expire dans ${moisRestants} mois`,
        description: 'Pense a lister les reserves non levees avant expiration.',
        badge: formatDateCourt(g.dateExpiration),
        badgeBg: '#FAEEDA',
        badgeText: '#854F0B',
        boutons: [
          { label: `SENTINELLE — preparer le courrier ${g.label}`, lien: '/vigie/sentinelle',
            messagePreRempli: `Ma garantie ${g.label} expire dans environ ${Math.ceil(g.joursRestants / 30)} mois. Je veux preparer un courrier pour lister mes reserves non levees avant expiration. Aide-moi.`,
            style: 'primaire' as const },
        ],
      });
    }
  }

  // Orange : echeance 10-30j
  const echProches = data.echeances.filter(e => e.date > dans10J && e.date <= dans30J);
  if (echProches.length > 0) {
    const e = echProches[0];
    signaux.push({
      id: 'echeance-proche',
      niveau: 'orange',
      icone: <CalendarClock className="w-4 h-4" style={{ color: '#854F0B' }} />,
      iconeBg: '#FAEEDA',
      titre: `Echeance le ${formatDateCourt(e.date)}`,
      description: e.label,
      badge: `dans ${e.joursRestants}j`,
      badgeBg: '#FAEEDA',
      badgeText: '#854F0B',
      boutons: [
        { label: 'Voir le calendrier', lien: '/vigie/calendrier', style: 'secondaire' },
      ],
    });
  }

  // Gris : documents manquants
  if (data.docsManquants > 0) {
    signaux.push({
      id: 'docs-manquants',
      niveau: 'gris',
      icone: <FileQuestion className="w-4 h-4" style={{ color: '#5F5E5A' }} />,
      iconeBg: '#F1EFE8',
      titre: `${data.docsManquants} document${data.docsManquants > 1 ? 's manquants' : ' manquant'} dans votre dossier`,
      description: data.labelsManquants.length > 0
        ? `Manquant${data.labelsManquants.length > 1 ? 's' : ''} : ${data.labelsManquants.slice(0, 3).join(', ')}${data.labelsManquants.length > 3 ? '...' : ''}`
        : 'Completez votre dossier pour que SENTINELLE soit pleinement efficace.',
      boutons: [
        ...(data.labelsManquants.length > 0 ? [{
          label: 'SENTINELLE — comment obtenir ces documents ?',
          lien: '/vigie/sentinelle',
          messagePreRempli: `Il me manque ${data.labelsManquants.length} document${data.labelsManquants.length > 1 ? 's' : ''} : ${data.labelsManquants.join(', ')}. Comment puis-je les obtenir ?`,
          style: 'primaire' as const,
        }] : []),
      ],
    });
  }

  // Bleu : quota < 20%
  if (data.quota) {
    const restant = data.quota.analysesMax - data.quota.analysesUtilisees;
    const pct = Math.round((restant / data.quota.analysesMax) * 100);
    if (pct < 20 && pct >= 0) {
      signaux.push({
        id: 'quota-faible',
        niveau: 'bleu',
        icone: <Sparkles className="w-4 h-4" style={{ color: '#185FA5' }} />,
        iconeBg: '#E6F1FB',
        titre: `Plus que ${restant} analyse${restant > 1 ? 's' : ''} disponible${restant > 1 ? 's' : ''}`,
        description: 'Pensez a recharger votre quota avant epuisement.',
        badge: `${restant}/${data.quota.analysesMax}`,
        badgeBg: '#E6F1FB',
        badgeText: '#185FA5',
        boutons: [
          { label: 'Voir le quota', lien: '/vigie/sentinelle', style: 'secondaire' },
        ],
      });
    }
  }

  // Tri Rouge > Orange > Bleu > Gris
  const ordre: Record<SignalAlerte['niveau'], number> = { rouge: 0, orange: 1, bleu: 2, gris: 3 };
  return signaux.sort((a, b) => ordre[a.niveau] - ordre[b.niveau]);
}

// ============================================
// COULEURS BORDURE PAR NIVEAU
// ============================================
const BORDURE: Record<SignalAlerte['niveau'], string> = {
  rouge:  '#E24B4A',
  orange: '#EF9F27',
  gris:   '#C9C8C2',
  bleu:   '#6BA3D6',
};

// ============================================
// COMPOSANT CARTE ALERTE (fidelee maquette)
// ============================================
const CarteAlerte: React.FC<{ signal: SignalAlerte }> = ({ signal }) => {
  const navigate = useNavigate();
  const bordure = BORDURE[signal.niveau];
  return (
    <div
      className="bg-white rounded-xl overflow-hidden flex gap-4 p-4 shadow-sm"
      style={{ borderLeft: `3px solid ${bordure}`, borderTop: '0.5px solid #E5E4E0', borderRight: '0.5px solid #E5E4E0', borderBottom: '0.5px solid #E5E4E0' }}
    >
      {/* Icone coloree */}
      <div
        className="flex-shrink-0 flex items-center justify-center rounded-lg w-9 h-9 mt-0.5"
        style={{ background: signal.iconeBg }}
      >
        {signal.icone}
      </div>

      {/* Contenu */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-sm font-medium text-gray-900">{signal.titre}</span>
          {signal.badge && (
            <span
              className="text-xs font-medium px-2 py-0.5 rounded-full"
              style={{ background: signal.badgeBg, color: signal.badgeText }}
            >
              {signal.badge}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-500">{signal.description}</p>
        {signal.boutons.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {signal.boutons.map((b, i) => (
              <button
                key={i}
                type="button"
                onClick={() => navigate(b.lien, b.messagePreRempli ? { state: { messagePreRempli: b.messagePreRempli } } : {})}
                className="inline-block text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors cursor-pointer"
                style={
                  b.style === 'primaire'
                    ? { color: '#185FA5', borderColor: '#B5D4F4', background: 'white' }
                    : { color: '#3A3935', borderColor: '#E5E4E0', background: 'white' }
                }
              >
                {b.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// COMPOSANT TUILE (fidelee maquette — stat en haut)
// ============================================
interface TuileProps {
  icone: React.ReactNode;
  iconeBg: string;
  stat: string;
  label: string;
  sub?: string;
  subColor?: string;
  lien: string;
}
const Tuile: React.FC<TuileProps> = ({ icone, iconeBg, stat, label, sub, subColor, lien }) => (
  <Link
    to={lien}
    className="block rounded-xl border p-3 cursor-pointer transition-colors hover:border-gray-300"
    style={{ background: 'white', borderColor: '#E5E4E0' }}
  >
    <div
      className="flex items-center justify-center rounded-lg mb-2"
      style={{ width: 28, height: 28, background: iconeBg }}
    >
      {icone}
    </div>
    <div className="text-xl font-medium text-gray-900 leading-tight">{stat}</div>
    <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    {sub && (
      <div className="text-[11px] mt-1" style={{ color: subColor || '#888780' }}>{sub}</div>
    )}
  </Link>
);

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const DashboardVigiePage: React.FC = () => {
  const { userProfile } = useAuth();
  const isAdminSaas = userProfile?.isGestionnaireSaas === true;
  const { coproprieteId, copropriete } = useCopropriete();
  const navigate = useNavigate();

  const rawNomCopro = copropriete?.nom || userProfile?.vigieInfo?.nomCopropriete || 'Ma copropriete';
  const nomCopro = typeof rawNomCopro === 'string' ? rawNomCopro : 'Ma copropriete';

  const adresseCoproStr = (() => {
    const adrPostale = (copropriete as any)?.adressePostale;
    const adrRaw = (copropriete as any)?.adresse;
    if (adrPostale && typeof adrPostale === 'object' && adrPostale.rue) {
      return [adrPostale.rue, [adrPostale.codePostal, adrPostale.ville].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    }
    if (adrRaw && typeof adrRaw === 'object' && adrRaw.rue) {
      return [adrRaw.rue, [adrRaw.codePostal, adrRaw.ville].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    }
    if (adrRaw && typeof adrRaw === 'string' && adrRaw.trim()) return adrRaw.trim();
    return undefined;
  })();

  // Etats
  const [quota, setQuota] = useState<QuotaVigie | null>(null);
  const [recentDocuments, setRecentDocuments] = useState<RecentDocument[]>([]);
  const [allCourriers, setAllCourriers] = useState<RecentCourrier[]>([]);
  const [profilBien, setProfilBien] = useState<ProfilBien | null>(null);
  const [echeances, setEcheances] = useState<EcheanceInfo[]>([]);
  const [signaux, setSignaux] = useState<SignalAlerte[]>([]);
  const [loading, setLoading] = useState(true);
  const [onboardingDone, setOnboardingDone] = useState(true);
  const [showResolus, setShowResolus] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [nbDocs, setNbDocs] = useState(0);
  const [nbContacts, setNbContacts] = useState<number | null>(null);
  const [nbDossiers, setNbDossiers] = useState<number | null>(null);

  const analysesPourcentage = quota
    ? Math.round((quota.analysesUtilisees / quota.analysesMax) * 100)
    : 0;

  // Chargement principal
  useEffect(() => {
    const loadData = async () => {
      if (!coproprieteId) { setLoading(false); return; }
      try {
        const done = await isOnboardingComplete(coproprieteId);
        setOnboardingDone(done);

        // Quotas
        const [quotaSnap, coproSnap] = await Promise.all([
          getDoc(doc(db, 'quotas', coproprieteId)),
          getDoc(doc(db, 'coproprietes', coproprieteId)),
        ]);
        const coproData = coproSnap.exists() ? coproSnap.data() : null;
        const sentinelleUsageRaw = coproData?.sentinelleUsage;
        const sentinelleUsage = (typeof sentinelleUsageRaw === 'object' && sentinelleUsageRaw !== null)
          ? (sentinelleUsageRaw.analysesEffectuees ?? 0)
          : (typeof sentinelleUsageRaw === 'number' ? sentinelleUsageRaw : 0);
        const sentinelleQuota = (typeof sentinelleUsageRaw === 'object' && sentinelleUsageRaw !== null)
          ? (sentinelleUsageRaw.quota ?? 100)
          : (coproData?.sentinelleQuota ?? 100);

        let quotaData: QuotaVigie | null = null;
        if (quotaSnap.exists()) {
          const qData = quotaSnap.data();
          quotaData = {
            analysesUtilisees: Math.max(qData.analysesUtilisees ?? 0, sentinelleUsage),
            analysesMax: qData.analysesMax ?? sentinelleQuota,
            stockageUtiliseMo: qData.stockageUtiliseMo ?? 0,
            stockageMaxMo: qData.stockageMaxMo ?? 2048,
            periodeDebut: qData.periodeDebut,
            periodeFin: qData.periodeFin,
            formule: qData.formule ?? 'vigie',
          };
        } else if (coproData) {
          quotaData = {
            analysesUtilisees: sentinelleUsage,
            analysesMax: sentinelleQuota,
            stockageUtiliseMo: 0,
            stockageMaxMo: 2048,
            periodeDebut: coproData.createdAt,
            periodeFin: null,
            formule: coproData.formule ?? 'vigie',
          };
        }
        setQuota(quotaData);

        // Mon bien
        let bien: ProfilBien | null = null;
        if (coproData) {
          const lots: LotInfo[] = [];
          let totalTantProprio = 0;
          if (Array.isArray(coproData.lots)) {
            for (const lot of coproData.lots) {
              let tantiemes = typeof lot.tantiemes === 'number' ? lot.tantiemes : 0;
              if (tantiemes === 0 && Array.isArray(lot.clesRepartition) && lot.clesRepartition.length > 0) {
                const firstCle = lot.clesRepartition[0];
                if (typeof firstCle?.tantiemes === 'number' && firstCle.tantiemes > 0) {
                  tantiemes = firstCle.tantiemes;
                }
              }
              lots.push({ nature: lot.nature || 'lot', tantiemes });
              totalTantProprio += tantiemes;
            }
          }
          const totalTantCopro: number | null =
            typeof coproData.totalTantiemes === 'number' && coproData.totalTantiemes > 0 ? coproData.totalTantiemes
            : typeof coproData.tantiemesTotal === 'number' && coproData.tantiemesTotal > 0 ? coproData.tantiemesTotal
            : null;
          const quotePart: number | null = totalTantCopro !== null && totalTantCopro > 0
            ? Math.round((totalTantProprio / totalTantCopro) * 10000) / 100 : null;
          const garanties: GarantieInfo[] = [];
          const maintenant = new Date();
          const parseGarantie = (label: string, dateStr?: string) => {
            if (!dateStr || typeof dateStr !== 'string') return;
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return;
            const joursRestants = Math.ceil((d.getTime() - maintenant.getTime()) / (1000 * 60 * 60 * 24));
            garanties.push({ label, dateExpiration: d, joursRestants });
          };
          if (coproData.garanties) {
            parseGarantie('GPA', coproData.garanties.gpa);
            parseGarantie('Biennale', coproData.garanties.biennale);
            parseGarantie('Decennale', coproData.garanties.decennale);
          }
          bien = { lots, totalTantiemes: totalTantProprio, totalTantiemesCopro: totalTantCopro, quotePart, nomSyndic: coproData.nomSyndic, coproprieteNeuve: coproData.coproprieteNeuve, garanties };
          setProfilBien(bien);
        }

        // Documents
        let docs: RecentDocument[] = [];
        let totalDocs = 0;
        try {
          const docsSnap = await getDocs(query(
            collection(db, 'documents'),
            where('coproprieteId', '==', coproprieteId),
            orderBy('createdAt', 'desc'),
            limit(50)
          ));
          totalDocs = docsSnap.size;
          docsSnap.docs.slice(0, 3).forEach(d => {
            const data = d.data();
            docs.push({
              id: d.id,
              titre: (typeof data.titre === 'string' ? data.titre : '') || (typeof data.nomFichier === 'string' ? data.nomFichier : '') || 'Document sans titre',
              type: (typeof data.type === 'string' ? data.type : '') || (typeof data.categorie === 'string' ? data.categorie : ''),
              createdAt: data.createdAt,
            });
          });
          setRecentDocuments(docs);
          setNbDocs(totalDocs);
        } catch {}

        // Dossiers
        try {
          const dossiersSnap = await getDocs(query(
            collection(db, 'affaires'),
            where('coproprieteId', '==', coproprieteId)
          ));
          setNbDossiers(dossiersSnap.size);
        } catch {}

        // Contacts
        try {
          const contactsSnap = await getDocs(query(
            collection(db, 'interlocuteurs'),
            where('coproprieteId', '==', coproprieteId)
          ));
          setNbContacts(contactsSnap.size);
        } catch {}

        // Courriers
        let courriers: RecentCourrier[] = [];
        try {
          const courriersSnap = await getDocs(query(
            collection(db, 'courriers'),
            where('coproprieteId', '==', coproprieteId),
            orderBy('createdAt', 'desc'),
            limit(100)
          ));
          courriersSnap.forEach(c => {
            const data = c.data();
            courriers.push({
              id: c.id,
              objet: typeof data.objet === 'string' ? data.objet : 'Courrier sans objet',
              statut: data.statut || 'brouillon',
              destinataire: typeof data.destinataire === 'string'
                ? data.destinataire
                : data.destinataire?.nom || data.destinataire?.raisonSociale || data.destinataireNom || '',
              createdAt: data.createdAt,
              updatedAt: data.updatedAt,
              envoyeAt: data.envoyeAt,
              dateEnvoi: data.dateEnvoi,
              resolu: data.resolu === true,
            });
          });
          setAllCourriers(courriers);
        } catch {}

        // Echeances
        let ech: EcheanceInfo[] = [];
        try {
          const now2 = new Date();
          const echSnap = await getDocs(query(
            collection(db, 'echeances'),
            where('coproprieteId', '==', coproprieteId),
            orderBy('date', 'asc'),
            limit(20)
          ));
          echSnap.forEach(e => {
            const data = e.data();
            if (!data.date) return;
            const d = data.date.toDate ? data.date.toDate() : new Date(data.date);
            if (d < now2) return;
            const joursRestants = Math.ceil((d.getTime() - now2.getTime()) / (1000 * 60 * 60 * 24));
            ech.push({ id: e.id, label: data.titre || data.label || 'Echeance', date: d, joursRestants });
          });
          setEcheances(ech);
        } catch {}

        // Checklist
        let docsManquants = 0;
        let labelsManquants: string[] = [];
        try {
          const cl = await chargerChecklistCockpit(coproprieteId);
          docsManquants = cl.checklist.totalManquants;
          labelsManquants = cl.checklist.items
            .filter(i => i.statut !== 'depose')
            .map(i => i.label);
        } catch {}

        // Signaux
        setSignaux(detecterSignaux({ courriers, echeances: ech, profilBien: bien, quota: quotaData, docsManquants, labelsManquants }));

      } catch (error) {
        console.error('Erreur chargement dashboard VIGIE:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coproprieteId, (copropriete as any)?.updatedAt?.toMillis?.(), refreshTrigger]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-3" />
          <p className="text-gray-500">Chargement de votre espace...</p>
        </div>
      </div>
    );
  }

  const recentCourriers3 = allCourriers.slice(0, 3);
  const nbBrouillons = allCourriers.filter(c => c.statut === 'brouillon').length;
  const quotaRestant = quota ? quota.analysesMax - quota.analysesUtilisees : 0;
  const prochaines5 = echeances.slice(0, 5);

  return (
    <div style={{ padding: '1.5rem', maxWidth: 1100, fontFamily: 'inherit' }}>

      {/* ============================================ */}
      {/* EN-TETE */}
      {/* ============================================ */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 500, color: '#1A1915' }}>
            {userProfile?.prenom ? `Bonjour ${userProfile.prenom}` : nomCopro}
          </div>
          <div style={{ fontSize: 13, color: '#6B6A65', marginTop: 2 }}>
            {isAdminSaas
              ? `Administrateur — ${nomCopro}`
              : adresseCoproStr
                ? `${nomCopro} · ${adresseCoproStr}`
                : nomCopro
            }
          </div>
        </div>
        <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 99, background: '#EAF3DE', color: '#3B6D11', marginTop: 4 }}>
          VIGIE actif
        </span>
      </div>

      {/* Banniere kit demarrage */}
      {!userProfile?.kitDemarrageVu && (
        <div style={{ background: 'linear-gradient(to right, #FFFBEB, #FFF7ED)', border: '0.5px solid #FCD34D', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ padding: 8, background: '#FEF3C7', borderRadius: 8, flexShrink: 0 }}>
            <BookOpen className="w-4 h-4 text-amber-600" />
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontWeight: 600, color: '#92400E', fontSize: 13 }}>Vous demarrez — par quoi commencer ?</p>
            <p style={{ color: '#B45309', fontSize: 12, marginTop: 2 }}>3 documents suffisent pour que SENTINELLE commence a vous proteger.</p>
          </div>
          <button
            onClick={() => navigate('/vigie/kit-demarrage')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: '#F59E0B', color: 'white', borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', flexShrink: 0 }}
          >
            Voir le guide <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Bandeau onboarding */}
      {!onboardingDone && (
        <button
          onClick={() => navigate('/vigie/sentinelle')}
          style={{ width: '100%', background: 'linear-gradient(to right, #2563EB, #1D4ED8)', borderRadius: 12, padding: '14px 18px', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16, textAlign: 'left' }}
        >
          <div style={{ padding: 8, background: 'rgba(255,255,255,0.2)', borderRadius: 8, flexShrink: 0 }}>
            <Shield className="w-5 h-5" />
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontWeight: 700, fontSize: 14 }}>SENTINELLE vous attend</p>
            <p style={{ color: '#BFDBFE', fontSize: 12, marginTop: 2 }}>Configurez votre protection en 10 minutes.</p>
          </div>
          <ArrowRight className="w-5 h-5" style={{ flexShrink: 0 }} />
        </button>
      )}

      {/* ============================================ */}
      {/* ZONE 1 — CE QUI ATTEND TON ATTENTION         */}
      {/* ============================================ */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, paddingBottom: 8, borderBottom: '1.5px solid #185FA5' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#185FA5', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Ce qui attend ton attention
        </span>
      </div>

      {signaux.length === 0 ? (
        <div
          style={{ background: 'var(--color-background-success, #F0FDF4)', border: '0.5px solid #BBF7D0', borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}
        >
          <div style={{ width: 40, height: 40, borderRadius: 12, background: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <CheckCircle className="w-5 h-5" style={{ color: '#16A34A' }} />
          </div>
          <div>
            <div style={{ fontWeight: 500, fontSize: 14, color: '#166534' }}>Rien d'urgent — SENTINELLE surveille pour toi</div>
            <div style={{ fontSize: 13, color: '#4B7C5A', marginTop: 2 }}>
              Tous tes courriers ont une reponse · Dossier complet · Aucune echeance dans les 30 jours
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
          {signaux.map(s => <CarteAlerte key={s.id} signal={s} />)}
        </div>
      )}

      {/* ============================================ */}
      {/* COURRIERS RESOLUS — section repliable        */}
      {/* Visible uniquement s'il y a au moins 1 résolu */}
      {/* ============================================ */}
      {(() => {
        const resolus = allCourriers.filter(c => c.resolu);
        if (resolus.length === 0) return null;
        return (
          <div style={{ marginBottom: 24 }}>
            <button
              onClick={() => setShowResolus(v => !v)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '4px 0', marginBottom: showResolus ? 10 : 0,
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 500, color: '#888780', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Courriers résolus ({resolus.length})
              </span>
              <ChevronDown
                style={{
                  width: 14, height: 14, color: '#888780',
                  transform: showResolus ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </button>
            {showResolus && (
              <div style={{ background: 'white', border: '0.5px solid #E5E4E0', borderRadius: 12, overflow: 'hidden' }}>
                {resolus.map((c, i) => (
                  <div
                    key={c.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 16px',
                      borderBottom: i < resolus.length - 1 ? '0.5px solid #E5E4E0' : 'none',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#5F5E5A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {c.objet}
                      </div>
                      <div style={{ fontSize: 11, color: '#888780', marginTop: 2 }}>
                        {c.destinataire ? `${c.destinataire} · ` : ''}Résolu
                      </div>
                    </div>
                    <Link
                      to={`/vigie/courriers/${c.id}`}
                      style={{ fontSize: 12, color: '#185FA5', textDecoration: 'none', flexShrink: 0 }}
                    >
                      Voir
                    </Link>
                    <button
                      onClick={async () => {
                        try {
                          await updateDoc(doc(db, 'courriers', c.id), { resolu: false });
                          setRefreshTrigger(v => v + 1);
                        } catch (err) {
                          console.error('Erreur réouverture courrier:', err);
                        }
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '4px 10px', borderRadius: 8,
                        background: '#F1EFE8', border: 'none', cursor: 'pointer',
                        fontSize: 12, color: '#5F5E5A', flexShrink: 0,
                      }}
                    >
                      <RotateCcw style={{ width: 12, height: 12 }} />
                      Rouvrir
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ============================================ */}
      {/* ZONE 2 — ACTIVITE RECENTE                    */}
      {/* ============================================ */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, paddingBottom: 8, borderBottom: '1.5px solid #185FA5' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#185FA5', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Activité récente
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 12, marginBottom: 20 }}>

        {/* Mes courriers */}
        <div style={{ background: 'white', border: '0.5px solid #E5E4E0', borderRadius: 12, padding: '14px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 15, fontWeight: 500, color: '#1A1915' }}>Mes courriers</span>
            <Link to="/vigie/documents" style={{ fontSize: 12, color: '#185FA5', textDecoration: 'none' }}>Voir tout →</Link>
          </div>
          {recentCourriers3.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#888780', fontSize: 13 }}>
              <p>Aucun courrier</p>
              <Link to="/vigie/sentinelle" style={{ color: '#185FA5', fontSize: 12, textDecoration: 'none' }}>Rediger avec SENTINELLE</Link>
            </div>
          ) : (
            recentCourriers3.map((c, i) => {
              const cfg = getStatutCourrierConfig(c.statut);
              return (
                <Link
                  key={c.id}
                  to={`/vigie/courriers/${c.id}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < recentCourriers3.length - 1 ? '0.5px solid #E5E4E0' : 'none', textDecoration: 'none' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#1A1915', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.objet}</div>
                    <div style={{ fontSize: 11, color: '#888780' }}>{c.destinataire ? `${c.destinataire} · ` : ''}{formatRelativeTime(c.createdAt)}</div>
                  </div>
                  <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 99, background: c.statut === 'envoye' ? '#EAF3DE' : '#F1EFE8', color: c.statut === 'envoye' ? '#3B6D11' : '#5F5E5A', flexShrink: 0 }}>
                    {cfg.label}
                  </span>
                </Link>
              );
            })
          )}
        </div>

        {/* Mes documents */}
        <div style={{ background: 'white', border: '0.5px solid #E5E4E0', borderRadius: 12, padding: '14px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 15, fontWeight: 500, color: '#1A1915' }}>Mes documents</span>
            <Link to="/vigie/documents" style={{ fontSize: 12, color: '#185FA5', textDecoration: 'none' }}>Voir tout →</Link>
          </div>
          {recentDocuments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#888780', fontSize: 13 }}>
              <p>Aucun document</p>
              <Link to="/vigie/sentinelle" style={{ color: '#185FA5', fontSize: 12, textDecoration: 'none' }}>Deposer via SENTINELLE</Link>
            </div>
          ) : (
            recentDocuments.map((d, i) => (
              <Link
                key={d.id}
                to={`/vigie/documents/${d.id}`}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < recentDocuments.length - 1 ? '0.5px solid #E5E4E0' : 'none', textDecoration: 'none' }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#1A1915', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.titre}</div>
                  <div style={{ fontSize: 11, color: '#888780' }}>
                    {d.type && <span style={{ textTransform: 'capitalize' }}>{d.type} · </span>}
                    {formatRelativeTime(d.createdAt)}
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>

      {/* ============================================ */}
      {/* ZONE 3 — MON ESPACE                          */}
      {/* ============================================ */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, paddingBottom: 8, borderBottom: '1.5px solid #185FA5' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#185FA5', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Mon espace
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 12 }}>

        {/* TUILES 3x2 */}
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 8, marginBottom: 8 }}>
            <Tuile
              lien="/vigie/dossiers"
              icone={<FolderOpen style={{ width: 14, height: 14, color: '#185FA5' }} />}
              iconeBg="#E6F1FB"
              stat={nbDossiers !== null ? String(nbDossiers) : "—"}
              label="Mes dossiers"
              sub={nbDossiers !== null ? `${nbDossiers} dossier${nbDossiers > 1 ? 's' : ''}` : 'Ouverts'}
            />
            <Tuile
              lien="/vigie/documents"
              icone={<MessageSquare style={{ width: 14, height: 14, color: '#854F0B' }} />}
              iconeBg="#FAEEDA"
              stat={String(allCourriers.length)}
              label="Mes courriers"
              sub={nbBrouillons > 0 ? `${nbBrouillons} brouillon${nbBrouillons > 1 ? 's' : ''} en attente` : 'Tout est envoye'}
              subColor={nbBrouillons > 0 ? '#854F0B' : '#888780'}
            />
            <Tuile
              lien="/vigie/documents"
              icone={<FileText style={{ width: 14, height: 14, color: '#3B6D11' }} />}
              iconeBg="#EAF3DE"
              stat={String(nbDocs)}
              label="Mes documents"
              sub={quota ? `GED · ${formatStockage(quota.stockageUtiliseMo)} utilises` : 'GED'}
            />
            <Tuile
              lien="/vigie/sentinelle"
              icone={<Sparkles style={{ width: 14, height: 14, color: '#534AB7' }} />}
              iconeBg="#EEEDFE"
              stat={quota ? `${quota.analysesUtilisees}/${quota.analysesMax}` : '—'}
              label="SENTINELLE"
              sub={quota ? `${quotaRestant} analyses restantes` : ''}
              subColor={analysesPourcentage >= 80 ? '#854F0B' : '#888780'}
            />
            <Tuile
              lien="/vigie/contacts"
              icone={<Users style={{ width: 14, height: 14, color: '#5F5E5A' }} />}
              iconeBg="#F1EFE8"
              stat={nbContacts !== null ? String(nbContacts) : "—"}
              label="Mes contacts"
              sub={nbContacts !== null ? `${nbContacts} contact${nbContacts > 1 ? 's' : ''}` : 'Syndic · Promoteur · KIC'}
            />
            <Tuile
              lien="/vigie/sentinelle"
              icone={<Home style={{ width: 14, height: 14, color: '#0F6E56' }} />}
              iconeBg="#E1F5EE"
              stat={profilBien?.quotePart !== null && profilBien?.quotePart !== undefined ? `${profilBien.quotePart}%` : '—'}
              label="Mon bien"
              sub={profilBien ? `${profilBien.totalTantiemes} tantiemes · ${profilBien.lots.length} lot${profilBien.lots.length > 1 ? 's' : ''}` : ''}
            />
          </div>

          {/* Lien comptabilite pleine largeur */}
          <Link
            to="/vigie/comptabilite"
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'white', border: '0.5px solid #E5E4E0', borderRadius: 12, textDecoration: 'none', transition: 'border-color 0.15s' }}
          >
            <div style={{ padding: 6, background: '#EEEDFE', borderRadius: 7, flexShrink: 0 }}>
              <Calculator style={{ width: 14, height: 14, color: '#534AB7' }} />
            </div>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#1A1915' }}>Ma comptabilite</span>
              <span style={{ fontSize: 11, color: '#888780', marginLeft: 8 }}>Appels de fonds · Suivi locatif</span>
            </div>
            <ChevronRight style={{ width: 14, height: 14, color: '#C9C8C2' }} />
          </Link>

        </div>

        {/* MINI-CALENDRIER */}
        <div style={{ background: 'white', border: '0.5px solid #E5E4E0', borderRadius: 12, padding: '14px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ fontSize: 15, fontWeight: 500, color: '#1A1915' }}>Prochaines echeances</span>
            <Link to="/vigie/calendrier" style={{ fontSize: 12, color: '#185FA5', textDecoration: 'none' }}>Voir tout →</Link>
          </div>

          {prochaines5.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#C9C8C2' }}>
              <CalendarClock style={{ width: 36, height: 36, margin: '0 auto 8px' }} />
              <p style={{ fontSize: 13, color: '#888780' }}>Aucune echeance dans les 30 jours</p>
              <Link to="/vigie/calendrier" style={{ fontSize: 12, color: '#185FA5', textDecoration: 'none' }}>Ajouter une echeance</Link>
            </div>
          ) : (
            prochaines5.map((e, i) => {
              const dot = e.joursRestants <= 10 ? '#E24B4A' : e.joursRestants <= 30 ? '#EF9F27' : '#888780';
              const dayColor = e.joursRestants <= 10 ? '#A32D2D' : '#1A1915';
              return (
                <Link
                  key={e.id}
                  to="/vigie/calendrier"
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    padding: '9px 0',
                    borderBottom: i < prochaines5.length - 1 ? '0.5px solid #E5E4E0' : 'none',
                    textDecoration: 'none',
                  }}
                >
                  {/* Date colonne */}
                  <div style={{ minWidth: 44, textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 500, color: dayColor, lineHeight: 1 }}>
                      {e.date.getDate()}
                    </div>
                    <div style={{ fontSize: 11, color: '#888780', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {e.date.toLocaleDateString('fr-FR', { month: 'short' })}
                    </div>
                  </div>
                  {/* Point couleur */}
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flexShrink: 0, marginTop: 5 }} />
                  {/* Label */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#1A1915' }}>{e.label}</div>
                    <div style={{ fontSize: 11, color: '#888780' }}>dans {e.joursRestants} jour{e.joursRestants > 1 ? 's' : ''}</div>
                  </div>
                </Link>
              );
            })
          )}

          {/* Garanties constructeur proches */}
          {profilBien && profilBien.garanties.filter(g => g.joursRestants > 0 && g.joursRestants <= 180).length > 0 && (
            <>
              <hr style={{ border: 'none', borderTop: '0.5px solid #E5E4E0', margin: '10px 0' }} />
              <div style={{ fontSize: 11, fontWeight: 500, color: '#888780', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Garanties constructeur
              </div>
              {profilBien.garanties.filter(g => g.joursRestants > 0 && g.joursRestants <= 180).map((g, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '5px 10px', borderRadius: 8, marginBottom: 4,
                    background: g.joursRestants <= 90 ? '#FCEBEB' : '#FAEEDA',
                    fontSize: 12,
                    color: g.joursRestants <= 90 ? '#A32D2D' : '#854F0B',
                  }}
                >
                  <span style={{ fontWeight: 500 }}>Garantie {g.label}</span>
                  <span>J-{g.joursRestants} · {formatDateCourt(g.dateExpiration)}</span>
                </div>
              ))}
            </>
          )}

          <hr style={{ border: 'none', borderTop: '0.5px solid #E5E4E0', margin: '10px 0' }} />
          <div style={{ textAlign: 'center', fontSize: 11, color: '#888780' }}>
            Rouge = moins de 10 jours · Orange = moins de 30 jours
          </div>
        </div>
      </div>

    </div>
  );
};

export default DashboardVigiePage;
